import {
  buildLegacyEffectOccurrenceKey,
  getLegacyEffectGlobalOccurrence,
} from './effectModel';

const toArray = (value) => Array.isArray(value) ? value : [];

const LEGACY_EFFECT_FIELDS = {
  buff: {
    temporary: 'buffList',
    permanent: 'permanentBuffs',
  },
  debuff: {
    temporary: 'debuffList',
    permanent: 'permanentDebuffs',
  },
};

const LEGACY_EFFECT_PATCH_FIELDS = [
  'buffList',
  'debuffList',
  'permanentBuffs',
  'permanentDebuffs',
  'buffSources',
  'buffData',
  'buffNotes',
];

const getLegacyEffectFieldName = (kind = 'buff', permanent = false) => (
  kind === 'debuff'
    ? (permanent ? LEGACY_EFFECT_FIELDS.debuff.permanent : LEGACY_EFFECT_FIELDS.debuff.temporary)
    : (permanent ? LEGACY_EFFECT_FIELDS.buff.permanent : LEGACY_EFFECT_FIELDS.buff.temporary)
);

export const getNextLegacyEffectDataKey = (text, char = {}) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return '';
  const allLists = [
    ...(char.permanentBuffs || []),
    ...(char.buffList || []),
    ...(char.permanentDebuffs || []),
    ...(char.debuffList || []),
  ];
  const count = allLists.filter((entry) => entry === normalizedText).length;
  return buildLegacyEffectOccurrenceKey(normalizedText, count);
};

export const buildLegacyEffectMetadata = ({
  source = null,
  playerTooltip = null,
  colorId = null,
  addedAt = null,
  addedBy = null,
  noteVisible = false,
  provenance = null,
  extra = null,
} = {}) => {
  const metadata = {
    source: source || null,
    playerTooltip: playerTooltip || null,
    color: colorId || null,
    addedAt: addedAt || new Date().toISOString(),
    addedBy: addedBy || null,
    noteVisible: noteVisible === true,
  };
  if (provenance) metadata.provenance = provenance;
  if (extra && typeof extra === 'object') Object.assign(metadata, extra);
  return metadata;
};

export const addLegacyEffect = (
  char = {},
  {
    text,
    kind = 'buff',
    permanent = false,
    allowDuplicates = true,
    metadata = null,
    buffSource = null,
    buffNote = null,
  } = {},
) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return char;

  const fieldName = getLegacyEffectFieldName(kind, permanent);
  const currentList = toArray(char[fieldName]);
  const alreadyPresent = currentList.includes(normalizedText);
  if (!allowDuplicates && alreadyPresent) return char;

  const nextChar = {
    ...char,
    [fieldName]: [...currentList, normalizedText],
  };

  if (metadata) {
    const dataKey = getNextLegacyEffectDataKey(normalizedText, char);
    nextChar.buffData = {
      ...(char.buffData || {}),
      [dataKey]: metadata,
    };
  }

  if (buffSource) {
    nextChar.buffSources = {
      ...(char.buffSources || {}),
      [normalizedText]: buffSource,
    };
  }

  if (buffNote) {
    nextChar.buffNotes = {
      ...(char.buffNotes || {}),
      [normalizedText]: buffNote,
    };
  }

  return nextChar;
};

export const removeLegacyEffect = (
  char = {},
  {
    text,
    kind = 'buff',
    permanent = null,
  } = {},
) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) return char;

  const nextChar = { ...char };
  const fieldNames = permanent === null
    ? [
        getLegacyEffectFieldName(kind, false),
        getLegacyEffectFieldName(kind, true),
      ]
    : [getLegacyEffectFieldName(kind, permanent)];

  fieldNames.forEach((fieldName) => {
    nextChar[fieldName] = toArray(nextChar[fieldName]).filter((entry) => entry !== normalizedText);
  });

  return nextChar;
};

const getPriorArraysForField = (fieldName, char = {}) => {
  switch (fieldName) {
    case 'permBuffs':
    case 'permanentBuffs':
      return [];
    case 'buffList':
      return [toArray(char.permanentBuffs)];
    case 'permDebuffs':
    case 'permanentDebuffs':
      return [toArray(char.permanentBuffs), toArray(char.buffList)];
    case 'debuffList':
      return [
        toArray(char.permanentBuffs),
        toArray(char.buffList),
        toArray(char.permanentDebuffs),
      ];
    default:
      return [];
  }
};

const reindexLegacyEffectData = (buffData = {}, text, totalBefore, removedOccurrence) => {
  const nextBuffData = { ...(buffData || {}) };
  const metadataByOccurrence = [];

  for (let occurrence = 0; occurrence < totalBefore; occurrence += 1) {
    const key = buildLegacyEffectOccurrenceKey(text, occurrence);
    metadataByOccurrence.push(nextBuffData[key]);
    delete nextBuffData[key];
  }

  metadataByOccurrence.splice(removedOccurrence, 1);
  metadataByOccurrence.forEach((metadata, occurrence) => {
    if (metadata === undefined) return;
    nextBuffData[buildLegacyEffectOccurrenceKey(text, occurrence)] = metadata;
  });

  return nextBuffData;
};

export const removeLegacyEffectAt = (
  char = {},
  {
    kind = 'buff',
    permanent = false,
    index = -1,
    text = null,
  } = {},
) => {
  const fieldName = getLegacyEffectFieldName(kind, permanent);
  const currentList = toArray(char[fieldName]);
  const targetIndex = Number.isInteger(index) ? index : -1;
  if (targetIndex < 0 || targetIndex >= currentList.length) return char;

  const targetText = String(text || currentList[targetIndex] || '').trim();
  if (!targetText) return char;

  const priorArrays = getPriorArraysForField(fieldName, char);
  const removedOccurrence = getLegacyEffectGlobalOccurrence(
    targetText,
    targetIndex,
    currentList,
    ...priorArrays,
  );
  const totalBefore = [
    ...(char.permanentBuffs || []),
    ...(char.buffList || []),
    ...(char.permanentDebuffs || []),
    ...(char.debuffList || []),
  ].filter((entry) => entry === targetText).length;

  const nextList = currentList.filter((_, entryIndex) => entryIndex !== targetIndex);
  const nextChar = {
    ...char,
    [fieldName]: nextList,
  };

  if ((char.buffData || {}) && totalBefore > 0) {
    nextChar.buffData = reindexLegacyEffectData(char.buffData, targetText, totalBefore, removedOccurrence);
  }

  const stillExists = [
    ...(fieldName === 'permanentBuffs' ? nextList : (nextChar.permanentBuffs || [])),
    ...(fieldName === 'buffList' ? nextList : (nextChar.buffList || [])),
    ...(fieldName === 'permanentDebuffs' ? nextList : (nextChar.permanentDebuffs || [])),
    ...(fieldName === 'debuffList' ? nextList : (nextChar.debuffList || [])),
  ].some((entry) => entry === targetText);

  if (!stillExists) {
    if (nextChar.buffSources?.[targetText] !== undefined) {
      const nextSources = { ...(nextChar.buffSources || {}) };
      delete nextSources[targetText];
      nextChar.buffSources = nextSources;
    }
    if (nextChar.buffNotes?.[targetText] !== undefined) {
      const nextNotes = { ...(nextChar.buffNotes || {}) };
      delete nextNotes[targetText];
      nextChar.buffNotes = nextNotes;
    }
  }

  return nextChar;
};

export const getLegacyEffectPatch = (before = {}, after = {}, fields = LEGACY_EFFECT_PATCH_FIELDS) => {
  const patch = {};
  fields.forEach((fieldName) => {
    if (after[fieldName] !== before[fieldName]) {
      patch[fieldName] = after[fieldName];
    }
  });
  return patch;
};

export const buildSystemLegacyEffectUpdates = (
  char = {},
  {
    effect,
    kind = 'buff',
    permanent = false,
    remove = false,
    sourceName = 'System',
  } = {},
) => {
  const normalizedText = String(effect || '').trim();
  if (!normalizedText) return {};

  const nextChar = remove
    ? removeLegacyEffect(char, {
        text: normalizedText,
        kind,
        permanent,
      })
    : addLegacyEffect(char, {
        text: normalizedText,
        kind,
        permanent,
        allowDuplicates: false,
        buffSource: kind === 'buff' && !permanent ? sourceName : null,
      });

  return getLegacyEffectPatch(char, nextChar, [
    'buffList',
    'debuffList',
    'permanentBuffs',
    'permanentDebuffs',
    'buffSources',
  ]);
};
